import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { getURL } from '../../utils/api';

const GENDER_OPTIONS = ['Male', 'Female', 'Other'];
const DEBOUNCE_MS = 500;

// Everything a Student ID lookup can fill in (or needs to clear out again).
const EMPTY_PROFILE = { fullName: '', telephone: '', email: '', dob: '', gender: '', nicNumber: '', address: '' };

const inputClass =
  'w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100';
// Applied once a Student ID lookup fills these fields from an existing
// record -- greyed out and non-editable rather than just visually similar,
// so it's clear at a glance these came from the existing record.
const disabledInputClass =
  'w-full rounded-lg border border-slate-200 bg-slate-100 px-3.5 py-2.5 text-sm text-slate-500 outline-none cursor-not-allowed';
const labelClass = 'mb-1.5 block text-xs font-semibold text-slate-600';

function Shell({ children }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 px-4 py-10">
      <div className="w-full max-w-lg">{children}</div>
    </div>
  );
}

function StatusCard({ icon, title, message, tone = 'slate', action }) {
  const toneClass = {
    slate: 'bg-slate-100 text-slate-500',
    red: 'bg-red-100 text-red-600',
    amber: 'bg-amber-100 text-amber-600',
    emerald: 'bg-emerald-100 text-emerald-600',
  }[tone];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
      <div className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ${toneClass}`}>{icon}</div>
      <h1 className="mt-4 text-lg font-bold text-slate-900">{title}</h1>
      <p className="mt-1.5 text-sm text-slate-500">{message}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export default function RegisterPoolJoin() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [invalidReason, setInvalidReason] = useState(null);
  const [batchLabel, setBatchLabel] = useState('');
  const [courseName, setCourseName] = useState('');

  const [form, setForm] = useState({
    studentId: '', registrationNumber: '',
    fullName: '', telephone: '', email: '', dob: '', gender: '', nicNumber: '', address: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  // studentId lookup: 'idle' | 'checking' | 'found' | 'not-found'
  const [studentLookup, setStudentLookup] = useState('idle');
  // registrationNumber check: null | 'checking' | { exists, sameBatch }
  const [regCheck, setRegCheck] = useState(null);

  const apiBase = getURL();

  useEffect(() => {
    let cancelled = false;
    axios.get(`${apiBase}/register-pool/public/sessions/${token}`)
      .then((resp) => {
        if (cancelled) return;
        setBatchLabel(resp.data?.batchLabel || '');
        setCourseName(resp.data?.courseName || '');
      })
      .catch((err) => {
        if (cancelled) return;
        setInvalidReason(err?.response?.data?.message || 'This registration link is invalid.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [apiBase, token]);

  const handleChange = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  // Student ID is the only field enabled on load. Everything else stays
  // locked until it's explicitly verified via the button (not just typed) --
  // editing the Student ID again after a verification invalidates it, so the
  // rest of the form locks back up until it's re-verified.
  const handleStudentIdChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, studentId: value }));
    setStudentLookup('idle');
  };

  const handleVerifyStudentId = () => {
    const studentId = form.studentId.trim();
    if (!studentId || studentLookup === 'checking') return;
    setStudentLookup('checking');
    axios.post(`${apiBase}/register-pool/public/sessions/${token}/lookup-student`, { studentId })
      .then((resp) => {
        if (resp.data?.found) {
          setStudentLookup('found');
          setForm((prev) => ({ ...prev, ...resp.data.student }));
        } else {
          // Not an existing student -- clear out anything a previous lookup
          // (for a different Student ID) may have filled in, so this is a
          // clean slate for a genuinely new registration.
          setStudentLookup('not-found');
          setForm((prev) => ({ ...prev, ...EMPTY_PROFILE }));
        }
      })
      .catch(() => setStudentLookup('idle'));
  };

  // Profile fields (name, phone, etc.) unlock only for a confirmed-new
  // Student ID -- a found record keeps them locked (auto-filled instead).
  // Registration Number unlocks either way, once verification has actually
  // run, since every student -- new or existing -- still has to provide it.
  const profileFieldsLocked = studentLookup !== 'not-found';
  const registrationNumberLocked = studentLookup === 'idle' || studentLookup === 'checking';

  // Live duplicate check on Registration Number.
  useEffect(() => {
    const registrationNumber = form.registrationNumber.trim();
    if (!registrationNumber) {
      setRegCheck(null);
      return undefined;
    }
    setRegCheck('checking');
    const timer = setTimeout(() => {
      axios.post(`${apiBase}/register-pool/public/sessions/${token}/check-registration`, { registrationNumber })
        .then((resp) => setRegCheck(resp.data))
        .catch(() => setRegCheck(null));
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [form.registrationNumber, apiBase, token]);

  // Only a duplicate in THIS batch blocks submission -- a match in a
  // different batch is shown as a heads-up but still lets them submit, since
  // the coordinator reviews and resolves it (removing whichever batch
  // registration turns out to be wrong).
  const registrationTakenSameBatch = regCheck && regCheck !== 'checking' && regCheck.exists && regCheck.sameBatch;
  const registrationTakenOtherBatch = regCheck && regCheck !== 'checking' && regCheck.exists && !regCheck.sameBatch;
  const canSubmit = !submitting && !registrationNumberLocked && regCheck !== 'checking' && !registrationTakenSameBatch;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName.trim()) {
      setSubmitError('Full name is required.');
      return;
    }
    if (!form.registrationNumber.trim()) {
      setSubmitError('Registration number is required.');
      return;
    }
    if (registrationTakenSameBatch) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      await axios.post(`${apiBase}/register-pool/public/sessions/${token}/submissions`, form);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err?.response?.data?.message || 'Failed to submit your details. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <div className="flex justify-center py-20">
          <svg className="h-8 w-8 animate-spin text-indigo-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        </div>
      </Shell>
    );
  }

  if (invalidReason) {
    return (
      <Shell>
        <StatusCard
          tone="red"
          icon={(
            <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          )}
          title="Link not available"
          message={invalidReason}
        />
      </Shell>
    );
  }

  if (submitted) {
    return (
      <Shell>
        <StatusCard
          tone="emerald"
          icon={(
            <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          )}
          title="Details submitted"
          message="Thank you! Your registration is now waiting for staff verification. You'll be contacted once it's confirmed."
        />
      </Shell>
    );
  }

  // Registration number already used for THIS batch -- no point showing the
  // rest of the form at all.
  if (regCheck && regCheck !== 'checking' && regCheck.exists && regCheck.sameBatch) {
    return (
      <Shell>
        <StatusCard
          tone="amber"
          icon={(
            <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          title="Already Registered"
          message="You are already registered for this batch. There's nothing further to submit."
          action={(
            <button
              type="button"
              onClick={() => { setForm((prev) => ({ ...prev, registrationNumber: '' })); setRegCheck(null); }}
              className="text-xs font-semibold text-indigo-600 hover:underline"
            >
              Not you? Try a different registration number
            </button>
          )}
        />
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-5 text-white">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-200">Student Registration</p>
          <h1 className="mt-0.5 text-lg font-bold">{batchLabel || 'New Batch'}</h1>
          {courseName && <p className="mt-0.5 text-sm text-indigo-100">{courseName}</p>}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-6">
          <p className="text-xs text-slate-500">
            Fill in your details below. Your registration will be reviewed and confirmed by our staff.
          </p>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Student ID</label>
              <div className="flex gap-2">
                <input
                  value={form.studentId}
                  onChange={handleStudentIdChange}
                  className={inputClass}
                  placeholder="If you already have one"
                />
                <button
                  type="button"
                  onClick={handleVerifyStudentId}
                  disabled={!form.studentId.trim() || studentLookup === 'checking'}
                  className="shrink-0 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {studentLookup === 'checking' ? '…' : 'Verify'}
                </button>
              </div>
              {studentLookup === 'found' && (
                <p className="mt-1 text-[11px] font-medium text-emerald-600">✓ Found — details filled in below.</p>
              )}
              {studentLookup === 'not-found' && (
                <p className="mt-1 text-[11px] text-slate-400">No existing record — fill in your details below.</p>
              )}
              {studentLookup === 'idle' && (
                <p className="mt-1 text-[11px] text-slate-400">Enter your Student ID (if you have one) and press Verify to continue.</p>
              )}
            </div>
            <div>
              <label className={labelClass}>Registration Number *</label>
              <input
                value={form.registrationNumber}
                onChange={handleChange('registrationNumber')}
                required
                disabled={registrationNumberLocked}
                className={registrationNumberLocked ? disabledInputClass : inputClass}
                placeholder="e.g. 012547"
              />
            </div>
          </div>

          {/* Same-batch duplicates short-circuit to their own screen above;
              only the different-batch case is ever seen here -- this doesn't
              block submission, it's just a heads-up for the coordinator. */}
          {registrationTakenOtherBatch && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700">
              This registration number is already registered to a different batch. You can still submit — your coordinator will review and resolve it.
            </p>
          )}

          <div>
            <label className={labelClass}>Full Name *</label>
            <input
              value={form.fullName}
              onChange={handleChange('fullName')}
              required
              disabled={profileFieldsLocked}
              className={profileFieldsLocked ? disabledInputClass : inputClass}
              placeholder="Your full name"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Telephone</label>
              <input
                value={form.telephone}
                onChange={handleChange('telephone')}
                disabled={profileFieldsLocked}
                className={profileFieldsLocked ? disabledInputClass : inputClass}
                placeholder="07XXXXXXXX"
              />
            </div>
            <div>
              <label className={labelClass}>Email</label>
              <input
                type="email"
                value={form.email}
                onChange={handleChange('email')}
                disabled={profileFieldsLocked}
                className={profileFieldsLocked ? disabledInputClass : inputClass}
                placeholder="you@example.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Date of Birth</label>
              <input
                type="date"
                value={form.dob}
                onChange={handleChange('dob')}
                disabled={profileFieldsLocked}
                className={profileFieldsLocked ? disabledInputClass : inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Gender</label>
              <select
                value={form.gender}
                onChange={handleChange('gender')}
                disabled={profileFieldsLocked}
                className={profileFieldsLocked ? disabledInputClass : inputClass}
              >
                <option value="">Select…</option>
                {GENDER_OPTIONS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className={labelClass}>NIC Number</label>
            <input
              value={form.nicNumber}
              onChange={handleChange('nicNumber')}
              disabled={profileFieldsLocked}
              className={profileFieldsLocked ? disabledInputClass : inputClass}
              placeholder="e.g. 200012345678"
            />
          </div>

          <div>
            <label className={labelClass}>Address</label>
            <textarea
              value={form.address}
              onChange={handleChange('address')}
              rows={3}
              disabled={profileFieldsLocked}
              className={`${profileFieldsLocked ? disabledInputClass : inputClass} resize-none`}
              placeholder="Your home address"
            />
          </div>

          {submitError && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700">{submitError}</p>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full rounded-lg bg-indigo-600 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Submitting…' : registrationTakenSameBatch ? 'Already Registered' : 'Submit for Verification'}
          </button>
        </form>
      </div>
      <p className="mt-4 text-center text-[11px] text-slate-400">Kaaryaalaya · Student Registration</p>
    </Shell>
  );
}
